import {
	RewardEngineOption,
	TreatmentSegment,
} from "../../domain/treatmentPlan.interface";

/**
 * SIMS-7151 (FR-3) — pure derivation logic for the plan-level segment accordion editor.
 *
 * Kept DOM-free so it is unit-tested directly (jest, no RTL): the accordion rendering itself lives
 * in ./index.tsx (a component index, excluded from coverage), and calls into these helpers. All
 * functions are immutable — they never mutate the passed segments array. Synthetic data only.
 */

/** The empty display value for a reward engine that is unset / not found. */
export const NO_REWARD_ENGINE = "None";

/** The build's empty-state sentence, shown wherever a list has no segments (template or activity). */
export const NO_SEGMENTS_MESSAGE =
	"No segments defined. This is a single-phase program. Add a segment to define a multi-segment phase structure.";

/** True when the plan actually has one or more segments (absent and [] are treated identically). */
export function hasSegments(segments?: TreatmentSegment[]): boolean {
	return Array.isArray(segments) && segments.length > 0;
}

/**
 * The starting week for the NEXT segment: one past the last segment's end, or week 1 when there
 * are none. Keeps newly-added segments contiguous by default (full contiguity validation is 7167).
 */
export function nextSegmentStartWeek(segments: TreatmentSegment[]): number {
	if (!segments.length) {
		return 1;
	}
	const last = segments[segments.length - 1];
	return (last.weekRange?.end ?? 0) + 1;
}

/** A new structural-only segment spanning a single week starting at `startWeek`. */
export function makeSegment(startWeek: number): TreatmentSegment {
	return {
		weekRange: { start: startWeek, end: startWeek },
		visitsPerWeek: 1,
		minHoursBetweenVisits: 0,
	};
}

/** Append a new contiguous segment (AC1). */
export function appendSegment(
	segments: TreatmentSegment[],
): TreatmentSegment[] {
	return [...segments, makeSegment(nextSegmentStartWeek(segments))];
}

/** Remove the segment at `index` (immutably). */
export function removeSegment(
	segments: TreatmentSegment[],
	index: number,
): TreatmentSegment[] {
	return segments.filter((_, i) => i !== index);
}

/** Immutably patch the segment at `index`. */
export function updateSegment(
	segments: TreatmentSegment[],
	index: number,
	patch: Partial<TreatmentSegment>,
): TreatmentSegment[] {
	return segments.map((seg, i) => (i === index ? { ...seg, ...patch } : seg));
}

/**
 * The effective reward engine for a segment: its override if set, else the template default.
 * Forward-compatible with the 7159 override toggle (segments carry no override yet in 7151).
 */
export function effectiveRewardEngineId(
	segment: TreatmentSegment,
	templateDefaultRewardEngineId?: string,
): string | undefined {
	return segment.rewardEngineId || templateDefaultRewardEngineId || undefined;
}

/** Human-readable reward engine name for an id, or "None" when unset / not found. */
export function rewardEngineName(
	id: string | undefined,
	options: RewardEngineOption[],
): string {
	if (!id) {
		return NO_REWARD_ENGINE;
	}
	return options.find((opt) => opt.id === id)?.name ?? NO_REWARD_ENGINE;
}

/** "Weeks {start}–{end}" (en dash), or "Weeks —" when the range is missing. */
export function formatWeekRange(segment: TreatmentSegment): string {
	const wr = segment.weekRange;
	if (!wr || wr.start == null || wr.end == null) {
		return "Weeks —";
	}
	return `Weeks ${wr.start}–${wr.end}`;
}

/**
 * The collapsed accordion header (AC2): segment number, week range, visits/week, and the effective
 * reward engine name — separated by middots.
 */
export function segmentHeaderSummary(
	segment: TreatmentSegment,
	index: number,
	options: RewardEngineOption[],
	templateDefaultRewardEngineId?: string,
): string {
	const engine = rewardEngineName(
		effectiveRewardEngineId(segment, templateDefaultRewardEngineId),
		options,
	);
	return `Segment ${index + 1} · ${formatWeekRange(segment)} · ${segment.visitsPerWeek}/wk · ${engine}`;
}

/**
 * Extract the persisted segments for pre-population (AC3). The backend folds the plan's segments
 * into treatmentConfig.segments (read shape: response.data.treatmentConfig.segments); a
 * non-segmented plan has an absent / non-array value, which yields [] (same as empty — AC4).
 */
export function readPlanSegments(
	treatmentConfig?: Record<string, unknown>,
): TreatmentSegment[] {
	const raw = treatmentConfig?.segments;
	return Array.isArray(raw) ? (raw as TreatmentSegment[]) : [];
}

// ---------------------------------------------------------------------------
// SIMS-9222 (FR-1, D1) — Program Segments belong to a treatment config TEMPLATE.
//
// The rule, mirrored from the backend's template-segments.helper.ts: a template's OWN list (a
// `segments` key, even an empty one) wins; a template without one inherits the plan-level list
// (`treatmentConfig.segments`) a plan saved before this change carries; otherwise none. Nothing is
// migrated: a template gains a key only when its list is edited.
// ---------------------------------------------------------------------------

interface TemplateWithSegments {
	_id?: string;
	name?: string;
	default?: boolean;
	segments?: unknown;
}

/** The segments that apply to `template` on a plan whose own config is `treatmentConfig`. */
export function readTemplateSegments(
	treatmentConfig: Record<string, unknown> | undefined,
	template: TemplateWithSegments | undefined,
): TreatmentSegment[] {
	if (template && Array.isArray(template.segments)) {
		return template.segments as TreatmentSegment[];
	}
	return readPlanSegments(treatmentConfig);
}

/**
 * What Update Template does with the template's `segments` key.
 *
 * Untouched: nothing — the template keeps exactly what it had (AC2, NFR-1). Edited: the list is
 * written. A list cleared on a plan with no legacy list leaves no key at all (an empty key would
 * read as a configured list); cleared on a legacy plan it is written as `[]`, so the template stops
 * inheriting the plan-level phases.
 */
export function templateSegmentsToCommit(
	template: TemplateWithSegments,
	planTreatmentConfig: Record<string, unknown> | undefined,
	dirty: boolean,
): { write: boolean; segments?: TreatmentSegment[]; remove?: boolean } {
	if (!dirty) {
		return { write: false };
	}
	const segments = Array.isArray(template.segments)
		? (template.segments as TreatmentSegment[])
		: [];
	if (
		segments.length === 0 &&
		readPlanSegments(planTreatmentConfig).length === 0
	) {
		return { write: false, remove: true };
	}
	return { write: true, segments };
}

interface PlanWithTemplates<
	T extends TemplateWithSegments = TemplateWithSegments,
> {
	treatmentConfig?: Record<string, unknown> | null;
	treatmentConfigOptions?: { treatmentConfigTemplates?: T[] } | null;
}

/**
 * A plan's template by the reference a form or an enrollment carries: its `_id`, a positional key
 * (`template_N`, the registration form's option key), or — when neither is given — its name. A
 * custom plan never was a template.
 */
export function findPlanTemplate<T extends TemplateWithSegments>(
	plan: PlanWithTemplates<T> | null | undefined,
	ref: string | undefined,
	name?: string,
): T | undefined {
	const templates =
		plan?.treatmentConfigOptions?.treatmentConfigTemplates ?? [];
	if (ref === "custom") {
		return undefined;
	}
	if (ref) {
		const positional = /^template_(\d+)$/.exec(ref);
		if (positional) {
			return templates[Number(positional[1])];
		}
		return templates.find((t) => t._id === ref);
	}
	return name ? templates.find((t) => t.name === name) : undefined;
}

/** The plan's default template (the one flagged default, else the first). */
export function defaultTemplateOf<T extends TemplateWithSegments>(
	plan: PlanWithTemplates<T> | null | undefined,
): T | undefined {
	const templates =
		plan?.treatmentConfigOptions?.treatmentConfigTemplates ?? [];
	return templates.find((t) => t.default) ?? templates[0];
}

// ---------------------------------------------------------------------------
// SIMS-9225 (FR-16, D1) — an additional activity's own Program Segments.
//
// An activity with a `segments` key (even an empty one) runs on its own list; an activity without
// one follows its template's. The key is written only when the administrator defines a list, so an
// untouched activity keeps no key (SIMS-9226 AC3 removes it to follow the template again).
// ---------------------------------------------------------------------------

/** True when the activity has its own list (a `segments` key, even []), false when it follows. */
export function activityHasOwnSegments(activity?: {
	segments?: unknown;
}): boolean {
	return Array.isArray(activity?.segments);
}

/**
 * The activity's own list when DEFINE OWN SEGMENTS is chosen (AC3): a one-time copy of the
 * template's list as it is in the form at that moment. Each copy drops its `segmentId` — the copies
 * are new phases, and the backend mints them ids of their own (SIMS-9223) — and nothing is invented
 * when the template has none.
 */
export function seedActivitySegments(
	templateSegments: TreatmentSegment[],
): TreatmentSegment[] {
	return templateSegments.map(({ segmentId: _templatePhase, ...rest }) => ({
		...rest,
		weekRange: { ...rest.weekRange },
	}));
}

// ---------------------------------------------------------------------------
// SIMS-9226 (FR-3, FR-4) — showing whether an activity inherits, and going back to the template.
// ---------------------------------------------------------------------------

/** Whether an activity follows its template's list or holds its own (the `segments` key, D1). */
export type ActivitySegmentsState = "following" | "own";

export function activitySegmentsState(activity?: {
	segments?: unknown;
}): ActivitySegmentsState {
	return activityHasOwnSegments(activity) ? "own" : "following";
}

/** The row markers' wording (Hi-Fi v2 badge 15 — designer copy, no build string exists). */
export const FOLLOWS_TEMPLATE_MARKER = "Follows template segments";
export const OWN_SEGMENTS_MARKER = "Own segments";

/**
 * FOLLOW TEMPLATE SEGMENTS (AC3): the activity without its own list. The key is DELETED, never set
 * to `[]` — an empty list is an activity that has its own phases and chose none (ADR-0044).
 */
export function withoutOwnSegments<T extends { segments?: unknown }>(
	activity: T,
): Omit<T, "segments"> {
	const { segments: _own, ...rest } = activity;
	return rest;
}

/** The helper beside FOLLOW TEMPLATE SEGMENTS: what confirming it removes. */
export function followTemplateHelperText(count: number): string {
	if (count <= 0) {
		return "Removes this activity's own segment list.";
	}
	return `Removes this activity's ${count} segment${count === 1 ? "" : "s"}.`;
}

/**
 * Q-6 (= A): the prefix of the hint under an override switch that is off. Inside an activity's own
 * editor the value comes from the activity, so the hint names it; the template editor (no activity
 * name given) keeps the build string from SIMS-7159.
 */
export function inheritHintPrefix(activityName?: string): string {
	if (activityName === undefined) {
		return "Inherits template default";
	}
	const name = activityName.trim();
	return name ? `Inherits from ${name}` : "Inherits from this activity";
}

// ---------------------------------------------------------------------------
// SIMS-7159 (FR-4, FR-6) — per-segment override toggles + reset-ladder lock.
// ---------------------------------------------------------------------------

/** The optional per-segment override fields exposed as toggle-driven inputs (FR-4). */
export type SegmentOverrideField =
	| "rewardEngineId"
	| "behavior"
	| "cmVisitScheduleOptionIds"
	| "disenrollmentDaysThreshold";

/** The string-valued override fields (their empty value is ""); disenrollment is numeric. */
const STRING_OVERRIDE_FIELDS: SegmentOverrideField[] = [
	"rewardEngineId",
	"behavior",
	"cmVisitScheduleOptionIds",
];

const OVERRIDE_LABELS: Record<SegmentOverrideField, string> = {
	rewardEngineId: "reward engine",
	behavior: "behavior",
	cmVisitScheduleOptionIds: "visit schedule",
	disenrollmentDaysThreshold: "disenrollment threshold",
};

/**
 * An override is ACTIVE (toggle ON) when its key is present on the segment. Toggle OFF removes the
 * key so the field inherits the template default (AC1); an existing persisted override reads back
 * as ON (pre-population).
 */
export function isOverrideActive(
	segment: TreatmentSegment,
	field: SegmentOverrideField,
): boolean {
	return segment[field] !== undefined;
}

/** Set an override value on a segment (immutably) — toggle ON + edit persists to segments[n] (AC2). */
export function setOverride(
	segments: TreatmentSegment[],
	index: number,
	field: SegmentOverrideField,
	value: string | number,
): TreatmentSegment[] {
	return updateSegment(segments, index, {
		[field]: value,
	} as Partial<TreatmentSegment>);
}

/** Clear an override (immutably) — toggle OFF removes the key so nothing is persisted (AC1). */
export function clearOverride(
	segments: TreatmentSegment[],
	index: number,
	field: SegmentOverrideField,
): TreatmentSegment[] {
	return updateSegment(segments, index, {
		[field]: undefined,
	} as Partial<TreatmentSegment>);
}

/**
 * SIMS-7151 (AC1) — the STRUCTURAL fields must be usable before a plan can be saved.
 *
 * The `required` prop on those TextFields is presentational only: the form submits through
 * handleSubmit, not native validation, so an emptied field silently reached the server. And
 * because `patchNumber` coerces "" to 0, an emptied Visits Per Week persisted as 0 — which makes
 * `segmentVisitCount` zero, so `resolveSegmentConfig`'s cumulative pointer never advances past
 * that segment. The segment an admin just configured then NEVER APPLIES to any visit, with no
 * error anywhere. Found by E2E C15606.
 *
 * A reversed range (end < start) is rejected for the same reason: it yields a NEGATIVE visit
 * count, which is strictly worse than the zero case.
 *
 * Note `minHoursBetweenVisits` legitimately allows 0 (its input sets min={0}), so an emptied
 * value is indistinguishable from a deliberate 0 and cannot be treated as missing here.
 */
export function validateSegmentStructure(
	segments: TreatmentSegment[],
): string | null {
	for (let i = 0; i < segments.length; i++) {
		const seg = segments[i];
		// Typed `number | undefined` on purpose. The interface declares weekRange (and its
		// start/end) as required, but this validator exists precisely because the form can hand
		// us a half-filled segment — an emptied TextField yields undefined, and a plan loaded from
		// the server may predate the field. Declaring the widened type lets the `typeof` guards
		// below narrow to `number`, instead of asserting a type we do not actually have
		// (Number.isFinite takes `unknown` and narrows nothing).
		const start: number | undefined = seg.weekRange?.start;
		const end: number | undefined = seg.weekRange?.end;

		if (typeof start !== "number" || !Number.isFinite(start) || start < 1) {
			return `Segment ${i + 1}: start week is required and must be at least 1.`;
		}
		if (typeof end !== "number" || !Number.isFinite(end) || end < start) {
			return `Segment ${i + 1}: end week is required and cannot be before the start week.`;
		}
		// The two checks below deliberately have NO `typeof` guard, unlike start/end above. The
		// asymmetry is a TypeScript one, not a runtime one: start/end are read through optional
		// chaining into `number | undefined` locals, so TS needs the `typeof` to narrow them before
		// `<` is allowed, whereas visitsPerWeek/minHoursBetweenVisits are declared plain `number` on
		// TreatmentSegment and read directly, so nothing needs narrowing.
		//
		// DO NOT drop the Number.isFinite call as redundant — the two checks catch DIFFERENT inputs.
		// An emptied TextField never arrives here as "": patchNumber (SegmentEditor/index.tsx) maps
		// raw === "" to 0, so the emptied-in-the-form case is a 0 and it is the `< 1` comparison that
		// rejects it. What Number.isFinite covers is every shape that is not a usable number at all —
		// NaN (patchNumber's `Number(raw)` on a non-numeric string) and undefined/null (a plan loaded
		// from the server that predates the field, or a segment built outside this form). Those must
		// not fall through to the comparison alone: any comparison against NaN is false, and
		// `undefined < 1` is false too, so `< 1` / `< 0` on its own would accept all of them.
		// Number.isFinite is the right predicate because it does NOT coerce, unlike the global
		// isFinite — global isFinite(null) and isFinite("") are both true.
		if (!Number.isFinite(seg.visitsPerWeek) || seg.visitsPerWeek < 1) {
			return `Segment ${i + 1}: visits per week is required and must be at least 1.`;
		}
		if (
			!Number.isFinite(seg.minHoursBetweenVisits) ||
			seg.minHoursBetweenVisits < 0
		) {
			return `Segment ${i + 1}: min hours between visits is required and cannot be negative.`;
		}
	}
	return null;
}

/**
 * AC2 (C15598): an override that is toggled ON but left empty/invalid blocks save. Returns a
 * descriptive error for the first offending segment, or null when all active overrides have a
 * value. A string override is empty when it trims to ""; the disenrollment override must be > 0.
 */
export function validateSegmentOverrides(
	segments: TreatmentSegment[],
): string | null {
	for (let i = 0; i < segments.length; i++) {
		const seg = segments[i];
		for (const field of STRING_OVERRIDE_FIELDS) {
			const value = seg[field];
			if (value !== undefined && String(value).trim() === "") {
				return `Segment ${i + 1}: the ${OVERRIDE_LABELS[field]} override is enabled but has no value.`;
			}
		}
		if (
			seg.disenrollmentDaysThreshold !== undefined &&
			!(seg.disenrollmentDaysThreshold > 0)
		) {
			return `Segment ${i + 1}: the ${OVERRIDE_LABELS.disenrollmentDaysThreshold} override must be a positive number.`;
		}
	}
	return null;
}

/**
 * The previous segment's effective reward engine (its override, else the template default), or
 * undefined for the first segment (no previous to compare against).
 */
export function previousEffectiveRewardEngineId(
	segments: TreatmentSegment[],
	index: number,
	templateDefaultRewardEngineId?: string,
): string | undefined {
	if (index <= 0) {
		return undefined;
	}
	return effectiveRewardEngineId(
		segments[index - 1],
		templateDefaultRewardEngineId,
	);
}

/**
 * AC3/AC4 (FR-6): resetLadderOnEntry is LOCKED (forced true) when this segment's effective reward
 * engine differs from the previous segment's effective reward engine — a reward-engine change on
 * entry always resets the ladder. The first segment (no previous) is never locked. When the engines
 * match, the toggle is freely configurable.
 */
export function isResetLadderLocked(
	segments: TreatmentSegment[],
	index: number,
	templateDefaultRewardEngineId?: string,
): boolean {
	const previous = previousEffectiveRewardEngineId(
		segments,
		index,
		templateDefaultRewardEngineId,
	);
	if (previous === undefined) {
		return false;
	}
	const current = effectiveRewardEngineId(
		segments[index],
		templateDefaultRewardEngineId,
	);
	return current !== previous;
}

/**
 * The effective resetLadderOnEntry value: forced true when locked (AC3), otherwise the segment's
 * own toggle value (default false) (AC4).
 */
export function resolveResetLadderValue(
	segments: TreatmentSegment[],
	index: number,
	templateDefaultRewardEngineId?: string,
): boolean {
	if (isResetLadderLocked(segments, index, templateDefaultRewardEngineId)) {
		return true;
	}
	return segments[index].resetLadderOnEntry ?? false;
}

/** What sits in the hint slot under "Reset ladder on entry" (SIMS-9226 AC5, Q-5). */
export interface ResetLadderHint {
	locked: boolean;
	/** The hint or reason line; null when the switch is free and ON (nothing inherited to show). */
	text: string | null;
}

/**
 * SIMS-9226 AC5 — the reason a locked switch is locked, as persistent text (it replaces the
 * SIMS-7159 hover tooltip), or the inherits hint of a free switch that is off. Copy is the Hi-Fi's.
 * The reset default is fixed (off), so this hint keeps the build wording inside an activity too.
 */
export function resetLadderHint(
	segments: TreatmentSegment[],
	index: number,
	templateDefaultRewardEngineId: string | undefined,
	options: RewardEngineOption[],
): ResetLadderHint {
	const previous = previousEffectiveRewardEngineId(
		segments,
		index,
		templateDefaultRewardEngineId,
	);
	const current = effectiveRewardEngineId(
		segments[index],
		templateDefaultRewardEngineId,
	);
	// Segments are shown 1-based, so the PREVIOUS segment (array index - 1) is "Segment {index}".
	const previousLabel = index;
	if (isResetLadderLocked(segments, index, templateDefaultRewardEngineId)) {
		return {
			locked: true,
			text: `Locked on. ${rewardEngineName(current, options)} differs from Segment ${previousLabel}'s reward engine, so the ladder restarts when a participant enters this segment.`,
		};
	}
	if (segments[index].resetLadderOnEntry) {
		return { locked: false, text: null };
	}
	const hint = "Inherits template default: off";
	return {
		locked: false,
		text:
			previous !== undefined && previous === current
				? `${hint}. Same reward engine as Segment ${previousLabel}, so the ladder continues.`
				: hint,
	};
}

// ---------------------------------------------------------------------------
// SIMS-9184 (FR-1, FR-2, FR-9, FR-20, NFR-3) — "start the next segment in a new week".
// ---------------------------------------------------------------------------

/**
 * The control's contract with QA, fixed on SIMS-9184 so the Playwright cases bind to something
 * stable instead of a copy-matching regex. The test id survives a copy change; the label does not,
 * which is why both are exported and the id is what the specs select on.
 */
export const NEW_WEEK_CONTROL_TESTID = "segment-force-new-week-toggle";
export const NEW_WEEK_CONTROL_LABEL = "Start the next segment in a new week";
export const NEW_WEEK_CONTROL_HINT =
	"The rest of the week is closed when this segment ends, so the next one starts in a fresh week.";

/**
 * AC1 (FR-2): the control is offered only on a segment that has another segment AFTER it.
 *
 * Derived from array position at render time rather than stored, which is what lets SIMS-9185 AC3
 * and AC4 work with no save round trip: add a segment and the control appears on the one that is
 * now second-to-last; delete the successors and it disappears.
 *
 * An index outside the array is "not available" rather than a crash — the editor re-renders during
 * add/remove and must not throw mid-edit.
 */
export function isNewWeekControlAvailable(
	segments: TreatmentSegment[],
	index: number,
): boolean {
	// SIMS-9233 (FR-13, D8) lifted SIMS-9186's withdrawal on plans with several activities: a
	// segment list belongs to the template or to one activity, so the setting on it is that list's
	// own, and the lock closes the week for that activity only. Every list offers it alike.
	if (index < 0 || index >= segments.length) {
		return false;
	}
	return index < segments.length - 1;
}

/**
 * NFR-3: absent, null and false all mean off. Only an explicit `true` is on.
 *
 * Checked with `=== true` rather than truthiness so a `null` arriving from the API — which is a
 * legitimate "off" per NFR-3 — cannot read as anything else.
 */
export function resolveNewWeekValue(segment: TreatmentSegment): boolean {
	return segment.startNextSegmentInNewWeek === true;
}

/**
 * AC2/AC3: write the flag on ONE segment, leaving every other segment byte-identical.
 *
 * Off writes an explicit `false` rather than deleting the key: "the administrator turned it off"
 * and "the administrator never touched it" are different facts, and only the first should survive
 * a save. The second is AC4's case and is handled by never calling this — the schema declares no
 * default, so an untouched segment reaches the database with no key at all.
 */
export function setNewWeekFlag(
	segments: TreatmentSegment[],
	index: number,
	value: boolean,
): TreatmentSegment[] {
	if (index < 0 || index >= segments.length) {
		return segments;
	}
	return updateSegment(segments, index, { startNextSegmentInNewWeek: value });
}

// ---------------------------------------------------------------------------
// SIMS-7167 (FR-3, FR-5) — client-side week-range contiguity validation.
// ---------------------------------------------------------------------------

/** A contiguity violation, carrying the 0-based index of the offending segment for inline display. */
export interface SegmentContiguityError {
	index: number;
	message: string;
}

/**
 * Validate that segment week ranges are internally consistent: the first segment starts at week 1
 * (AC5), and each subsequent segment is contiguous with the previous one — no gap (AC2), no overlap
 * (AC3). Returns the FIRST violation (with the offending segment index) or null when valid (AC1).
 * Mirrors the backend validateSegmentWeekRanges (SIMS-7143) so the UI blocks before the server does.
 * Total-duration coverage is NOT checked here (derived from segments — SIMS-7487).
 */
export function validateSegmentContiguity(
	segments: TreatmentSegment[],
): SegmentContiguityError | null {
	if (segments.length === 0) {
		return null;
	}

	// AC5 — the first segment must start at week 1.
	if (segments[0].weekRange?.start !== 1) {
		return {
			index: 0,
			message: `Segment 1 must start at week 1, but starts at week ${segments[0].weekRange?.start ?? "?"}.`,
		};
	}

	for (let i = 1; i < segments.length; i++) {
		const previousEnd = segments[i - 1].weekRange?.end ?? 0;
		const currentStart = segments[i].weekRange?.start ?? 0;
		const expectedStart = previousEnd + 1;

		// AC2 — a gap: the current segment starts after the previous one ended + 1.
		if (currentStart > expectedStart) {
			return {
				index: i,
				message: `Gap before segment ${i + 1}: it must start at week ${expectedStart} (segment ${i} ends at week ${previousEnd}).`,
			};
		}

		// AC3 — an overlap: the current segment starts before the previous one ended + 1.
		if (currentStart < expectedStart) {
			return {
				index: i,
				message: `Segment ${i + 1} overlaps segment ${i}: it must start at week ${expectedStart} (segment ${i} ends at week ${previousEnd}).`,
			};
		}
	}

	return null;
}

/** The contiguity error message for the save-block, or null when the ranges are valid. */
export function contiguityErrorMessage(
	segments: TreatmentSegment[],
): string | null {
	return validateSegmentContiguity(segments)?.message ?? null;
}

/** A template or activity as the plan form holds it: a name, and maybe its own phase list. */
export interface PhaseListOwner {
	name?: string;
	segments?: TreatmentSegment[];
	additionalTreatmentConfigs?: (PhaseListOwner | null)[];
}

/** One list's first problem: structure, then overrides, then contiguity (SIMS-7151/7159/7167). */
function phaseListError(segments: unknown): string | null {
	if (!Array.isArray(segments)) {
		return null;
	}
	return (
		validateSegmentStructure(segments) ||
		validateSegmentOverrides(segments) ||
		contiguityErrorMessage(segments)
	);
}

/**
 * SIMS-9224 (PRD FR-16) — every phase list is validated on its own, at whichever level it is set:
 * each template's own list, then each of its activities' lists. Returns the FIRST problem, naming
 * the template and, for an activity list, the activity — or null. A template or activity with no
 * list passes. Mirrors the backend's templateSegmentsError, so the form blocks what the server
 * would refuse.
 */
export function planSegmentListsError(
	templates: (PhaseListOwner | null)[] | undefined,
): string | null {
	for (const template of templates ?? []) {
		const templateName = template?.name ?? "unnamed";
		const ownError = phaseListError(template?.segments);
		if (ownError) {
			return `Template "${templateName}": ${ownError}`;
		}
		for (const activity of template?.additionalTreatmentConfigs ?? []) {
			const activityError = phaseListError(activity?.segments);
			if (activityError) {
				return `Template "${templateName}", activity "${activity?.name ?? "unnamed"}": ${activityError}`;
			}
		}
	}
	return null;
}
